import { useCallback, useEffect, useMemo, useState } from 'react';

import { TagV2Size } from '@zola/zola-ui/src/components/Tag/TagV2.styles';

import cx from 'classnames';
import _uniq from 'lodash/uniq';
import LazyLoad from 'react-lazyload';

import Carousel from '~/components/common/zolaUI/Carousel/Carousel';
import { CAROUSEL_SETTINGS } from '~/util/carouselSettings';
import { getVendorIconV3 } from '~/util/vendorUtils';

import { ResponsiveVendorTagV2 } from '../../badges/ResponsiveVendorTagV2';
import CachedImage from '../../images/CachedImage';
import { VendorCardVendor } from '../util/types';

import styles from './vendorCardPhoto.module.less';

type VendorCardPhotoProps = {
  vendor: VendorCardVendor;

  /** Scale the card size when hovered, default true. */
  scaleOnHover: boolean;

  /** self-contained image scale, where containing div does not expand, img only "zooms in" */
  altScaleOnHover: boolean | undefined;

  showBadges: boolean;

  tagSize: TagV2Size;

  showCarousel: boolean;
  carouselPhotoIds: string[];
  lazyload: boolean;
  readOnly: boolean;
  scrollContainer: string | Element | undefined;
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
};

export const wasCardPhotoClick = (event: React.MouseEvent<HTMLElement>) => {
  const target = event.target as HTMLElement;
  if (!target) return false;

  const arrowClicked = target.className.indexOf('slick-arrow') >= 0;
  const dotClicked = target.className.indexOf('carousel-dot') >= 0;
  if (!arrowClicked && !dotClicked) {
    return true;
  }
  return false;
};

export const VendorCardPhoto: React.FC<VendorCardPhotoProps> = (props) => {
  const {
    scaleOnHover,
    altScaleOnHover,
    showBadges,
    tagSize,
    vendor,
    carouselPhotoIds,
    lazyload,
    showCarousel,
    readOnly,
    children,
    scrollContainer,
    className,
    onClick,
  } = props;

  const { taxonomyNodeKey, imageIds, badges, storefrontCoverId } = vendor;

  const [carouselCards, setCarouselCards] = useState<JSX.Element[]>([]);

  const getCarouselImages = useCallback(() => {
    if (imageIds?.length) {
      return imageIds;
    }

    if (carouselPhotoIds?.length) {
      return _uniq([storefrontCoverId, ...carouselPhotoIds]).filter(Boolean) as string[];
    }

    if (storefrontCoverId) {
      return [storefrontCoverId];
    }

    return [];
  }, [carouselPhotoIds, imageIds, storefrontCoverId]);

  useEffect(() => {
    if (getCarouselImages().length) {
      setCarouselCards(
        getCarouselImages()
          .slice(0, 5)
          .map((imageId: string, idx: number) => {
            return (
              <div className={styles.carouselPhoto} id={`photo-${idx}`} key={imageId}>
                <CachedImage
                  uuid={imageId}
                  srcSizes={[
                    { width: 200, height: 150 },
                    { width: 250, height: 188 },
                    { width: 300, height: 225 },
                    { width: 400, height: 300 },
                    { width: 500, height: 375 },
                    { width: 600, height: 450 },
                  ]}
                  fit="crop"
                  quality={60}
                  loading={lazyload || idx > 0 ? 'lazy' : 'eager'}
                />
              </div>
            );
          })
      );
    }
  }, [getCarouselImages, lazyload]);

  const coverPhotoDisplay = useMemo(
    () => (
      <div className={styles.carouselPhoto}>
        <div>
          {getCarouselImages().length ? (
            <CachedImage
              uuid={getCarouselImages()[0]}
              srcSizes={[
                { width: 200, height: 150 },
                { width: 250, height: 188 },
                { width: 300, height: 225 },
                { width: 400, height: 300 },
                { width: 500, height: 375 },
                { width: 600, height: 450 },
              ]}
              fit="crop"
              quality={60}
              loading={lazyload ? 'lazy' : 'eager'}
            />
          ) : (
            <div className={styles.icon}>{getVendorIconV3(taxonomyNodeKey || 'all')({})}</div>
          )}
        </div>
      </div>
    ),
    [getCarouselImages, lazyload, taxonomyNodeKey]
  );

  const getCoverImage = useCallback(() => {
    if (showCarousel && carouselCards.length > 1 && !readOnly) {
      return (
        <Carousel
          content={carouselCards}
          settings={CAROUSEL_SETTINGS}
          useV2ArrowStyles
          useSmallerV2Arrows
        />
      );
    }
    return coverPhotoDisplay;
  }, [showCarousel, carouselCards, coverPhotoDisplay, readOnly]);

  return (
    <div
      className={cx(
        styles.cardBody,
        {
          [styles.scaleOnHover]: !readOnly && scaleOnHover,
          [styles.altScaleOnHover]: !readOnly && altScaleOnHover,
        },
        className
      )}
      onClick={onClick}
    >
      {showBadges && <ResponsiveVendorTagV2 size={tagSize} badges={badges} />}
      <div className={cx(styles.carouselContainer)}>
        {lazyload ? (
          <LazyLoad height={225} offset={300} once scrollContainer={scrollContainer}>
            {getCoverImage()}
          </LazyLoad>
        ) : (
          getCoverImage()
        )}
      </div>
      {children}
    </div>
  );
};
